import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import SignIn from "../components/auth/SignIn";
import HomePage from "./HomePage";
import SolutionDetails from "./SolutionDetails/SolutionDetailsPage";
import FAQPage from "./FAQPage";
import ScrollToTop from '../components/ScrollToTop';
import SessionTimeoutWarningModal from '../components/SessionTimeoutWarningModal';
import oktaConfig from "../oktaConfig";
import { appConfig } from '../utils/config';
import { clearCachedSelfEntitlementStatus } from '../utils/selfEntitlementService';
import { saveUserInfo, saveUsersData } from '../utils/helpers';
import { trackUserLogin } from '../utils/analytics';
import { useActivityBasedTimeout } from '../hooks/useActivityBasedTimeout';


// Logout logic as a reusable function
export function handleLogout() {
  // Capture id_token BEFORE clearing anything (needed for Okta logout URL)
  const idToken = localStorage.getItem('id_token');

  // Guard against duplicate logout calls across tabs
  const isAlreadyLoggingOut = sessionStorage.getItem('logoutInProgress') === 'true';

  // --- ALWAYS do a full cleanup regardless of early-return path ---
  // Previously, early returns for isAlreadyLoggingOut / !idToken skipped all
  // the removeItem calls below, leaving stale data (accountId, email, etc.)
  // in localStorage. Moving cleanup here ensures it always runs.

  // Dispatch storage event FIRST so same-tab listeners react to loggedIn removal
  window.dispatchEvent(new StorageEvent('storage', {
    key: 'loggedIn',
    newValue: null,
    oldValue: 'true',
    storageArea: localStorage,
    url: window.location.href,
  }));

  // Clear all localStorage user/session data
  localStorage.removeItem('loggedIn');
  localStorage.removeItem('accountId');
  localStorage.removeItem('userName');
  localStorage.removeItem('email');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('usersData');
  localStorage.removeItem('entitlementsData');
  localStorage.removeItem('tenantsData');
  localStorage.removeItem('tokenExpiresAt');
  localStorage.removeItem('sessionCreated');
  localStorage.removeItem('expiresAt');
  localStorage.removeItem('idleExpiresAt');
  localStorage.removeItem('okta-cache-storage');
  localStorage.removeItem('okta-shared-transaction-storage');
  localStorage.removeItem('okta-token-storage');
  localStorage.removeItem('isBetaUser');
  localStorage.removeItem('id_token');
  try { localStorage.removeItem('authInProgress'); } catch {}
  try { localStorage.removeItem('authInProgressAt'); } catch {}
  try { localStorage.removeItem('justSignedIn'); } catch {}
  try { localStorage.removeItem('lastSessionHeartbeat'); } catch {}

  // Clear self-entitlement status cache
  clearCachedSelfEntitlementStatus();
  localStorage.removeItem('marketplace_solutions_catalog');

  // Broadcast to other tabs to clear justSignedIn
  try { const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('marketplace-session') : null; bc?.postMessage({ type: 'clearJustSignedIn' }); try { bc?.close(); } catch {} } catch {}

  // Clear sessionStorage items
  sessionStorage.removeItem('okta-transaction-storage');
  sessionStorage.removeItem('postLoginRedirect');
  sessionStorage.removeItem('loggedIn');
  sessionStorage.removeItem('pkce_code_verifier');
  sessionStorage.removeItem('userName');
  sessionStorage.removeItem('userInfo');
  sessionStorage.removeItem('usersData');
  sessionStorage.removeItem('tokenExpiresAt');
  sessionStorage.removeItem('idleExpiresAt');
  sessionStorage.removeItem('marketplace_filter_state'); 

  // Clear all cookies (frontend-side cleanup as safety net)
  document.cookie.split(';').forEach((c) => {
    document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
  });

  // --- Now handle the redirect path ---

  // If another tab already initiated logout, just redirect without calling Okta again
  // (calling Okta logout a second time would fail with 400 due to missing id_token_hint)
  if (isAlreadyLoggingOut) {
    window.location.href = appConfig.logout.redirectUri || '/';
    return;
  }

  // If id_token is missing (Okta cleared it, or another tab already used it),
  // skip Okta logout call and redirect directly to home
  if (!idToken) {
    window.location.href = appConfig.logout.redirectUri || '/';
    return;
  }

  // Mark that this tab is initiating Okta logout
  sessionStorage.setItem('logoutInProgress', 'true');
  
  // Redirect to Okta logout endpoint with id_token_hint
  const baseLogoutUrl = appConfig.logout.baseUrl;
  const fallbackUrl = appConfig.logout.redirectUri || '/';
  const postLogoutRedirectUri = encodeURIComponent(fallbackUrl);

  // Set a hard fallback: if we haven't redirected in 5 seconds total, force it
  // This handles the case where iframe events don't fire reliably
  const hardTimeoutId = setTimeout(() => {
    try { window.location.href = fallbackUrl; } catch {}
    try { window.location.replace(fallbackUrl); } catch {}
  }, 5000);
  
  // Attempt Okta logout with resilient fallback
  // Use hidden iframe to detect 400/error responses and fallback gracefully
  const logoutUrl = `${baseLogoutUrl}?id_token_hint=${idToken}&post_logout_redirect_uri=${postLogoutRedirectUri}`;
  
  // Create a hidden iframe to attempt Okta logout
  // This allows us to detect errors (like 400) and fallback to home page
  // instead of showing an error page to the user
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.setAttribute('aria-hidden', 'true');
  
  let handledRedirect = false;
  
  // Timeout fallback: if iframe doesn't complete in 3 seconds, redirect to home
  // This handles network issues, slow responses, or blocked requests
  const timeoutId = setTimeout(() => {
    if (handledRedirect) return;
    handledRedirect = true;
    clearTimeout(hardTimeoutId);
    try { document.body.removeChild(iframe); } catch {}
    window.location.href = fallbackUrl;
    window.location.replace(fallbackUrl);
  }, 3000);
  
  // Handle successful load or error
  iframe.onload = () => {
    if (handledRedirect) return;
    handledRedirect = true;
    clearTimeout(hardTimeoutId);
    clearTimeout(timeoutId);
    try { document.body.removeChild(iframe); } catch {}
    window.location.href = fallbackUrl;
    window.location.replace(fallbackUrl);
  };
  
  iframe.onerror = () => {
    if (handledRedirect) return;
    handledRedirect = true;
    clearTimeout(hardTimeoutId);
    clearTimeout(timeoutId);
    try { document.body.removeChild(iframe); } catch {}
    window.location.href = fallbackUrl;
    window.location.replace(fallbackUrl);
  };
  
  // Start the logout attempt
  document.body.appendChild(iframe);
  iframe.src = logoutUrl;
}


function Callback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginAttempted, setLoginAttempted] = useState(false);

  useEffect(() => {
    if (loginAttempted) return;
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    let codeVerifier = null;
    const oktaTx = sessionStorage.getItem("okta-transaction-storage");
    if (oktaTx) {
      try {
        const txObj = JSON.parse(oktaTx);
        codeVerifier = txObj.codeVerifier || null;
      } catch (e) {
        // console.error("Failed to parse okta-transaction-storage:", e);
      }
    }
    const redirectUri = oktaConfig.redirectUri;
    const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
    const redirectTo = postLoginRedirect || "/";
    
    if (code && codeVerifier) {
      setLoginAttempted(true);
      sessionStorage.removeItem("pkce_code_verifier");
      sessionStorage.removeItem('postLoginRedirect');

      fetch(`${appConfig.api.baseUrl}${appConfig.api.loginEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code, codeVerifier, redirectUri })
      })
        .then(async (response) => {
          if (!response.ok) {
            // API call failed, do not log in
            navigate(redirectTo);
            return;
          }
          const data = await response.json();
          if (data && data.userName) {
            // Track successful login
            trackUserLogin('okta_sso', true, 'marketplace_user');
            
            localStorage.setItem("isBetaUser", data?.isBetaUser ? "true" : "false");
            localStorage.setItem('userName', data.userName);
            localStorage.setItem('loggedIn', 'true');
            
            // Store token expiry from backend response
            if (data.expiresIn) {
              const expiresAt = Date.now() + (data.expiresIn * 1000);
              localStorage.setItem('tokenExpiresAt', expiresAt.toString()); // Use localStorage for consistency
            }
            
            // Store absolute session start time (for 2-hour limit tracking)
            if (data.sessionCreated) {
              const sessionCreatedMs = data.sessionCreated * 1000; // Backend sends Unix timestamp in seconds
              localStorage.setItem('sessionCreated', sessionCreatedMs.toString());
            }
            
            // Store entitlements in localStorage (persists like login state)
            if (data.entitlementsData && Array.isArray(data.entitlementsData)) {
              localStorage.setItem('entitlementsData', JSON.stringify(data.entitlementsData));
            } else {
              // Clear entitlements if none provided
              localStorage.removeItem('entitlementsData');
            }
            
            // Store tenantsData in localStorage (persists like login state)
            if (data.tenantsData && Array.isArray(data.tenantsData)) {
              localStorage.setItem('tenantsData', JSON.stringify(data.tenantsData));
            } else {
              // Clear tenantsData if none provided
              localStorage.removeItem('tenantsData');
            }
            
            if (data.idToken) {
              localStorage.setItem('id_token', data.idToken);
            }
            
            // Save user profile information for form pre-filling
            if (data.userInfo) {
              const filteredUserInfo = {
                given_name: data.userInfo.given_name,
                family_name: data.userInfo.family_name,
                email: data.userInfo.email,
                locale: data.userInfo.locale
              };
              saveUserInfo(filteredUserInfo);
            }
            
            // Save users data
            if (data.usersData && Array.isArray(data.usersData)) {
              const filteredUsersData = data.usersData.map((user: any) => ({
                firstname: user.firstname,
                lastname: user.lastname,
                email: user.email
              }));
              saveUsersData(filteredUsersData);
            }
            
            // Mark that we've just completed sign-in so the session hook can
            // suppress modal re-appearance until the server-side cookie is set.
            try { localStorage.setItem('justSignedIn', Date.now().toString()); } catch {}
            try { localStorage.removeItem('authInProgress'); } catch {}
            try { localStorage.removeItem('authInProgressAt'); } catch {}
            // Redirect or update UI as needed
            navigate('/');
          } else {
            // Missing userName, treat as failed login
            trackUserLogin('okta_sso', false, 'marketplace_user');
            navigate(redirectTo);
          }
        })
        .catch((error) => {
          // Track failed login
          trackUserLogin('okta_sso', false, 'marketplace_user');
          navigate(redirectTo);
        });
    } else {
      navigate(redirectTo);
    }
  }, [location, navigate, loginAttempted]);

  return <div style={{ display: "none" }}>Signing in…</div>;
}

const RouterPage: React.FC = () => {
  // Use activity-based timeout hook for automatic token refresh on activity
  // This replaces the old localStorage.expiresAt polling approach
  const { showWarningModal, warningSecondsRemaining, warningType, handleStayLoggedIn, dismissWarning } = useActivityBasedTimeout(handleLogout);

  const startOktaLogin = () => {
    // Hide the warning modal immediately to avoid it sticking on screen
    try { dismissWarning(); } catch {}
    // Mark that auth redirect is in progress so we don't auto-logout during navigation
    try {
      localStorage.setItem('authInProgress', 'true');
      localStorage.setItem('authInProgressAt', Date.now().toString());
    } catch {}
    // Use the existing SignIn route which handles PKCE/Okta redirect
    window.location.href = '/details/sign-in';
  };

  return (
    <>
      <SessionTimeoutWarningModal
        open={showWarningModal}
        secondsRemaining={warningSecondsRemaining}
        warningType={warningType}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogout}
        onDismiss={dismissWarning}
        onSignIn={startOktaLogin}
      />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Home route: main landing page */}
          <Route path="/" element={<HomePage />} />
          {/* Authentication routes */}
          <Route path="/details/sign-in" element={<SignIn />} />
          {/* Solution details page (dynamic :id param) */}
          <Route path="/details/:id" element={<SolutionDetails />} />
          {/* FAQ page */}
          <Route path="/faq" element={<FAQPage />} />
          {/* Logout route: redirect user to home page */}
          <Route path="/logout" element={<Navigate to="/" replace />} />
          {/* Catch-all route for undefined paths (optional) */}
          <Route path="/login/callback" element={<Callback />} />
        </Routes>
      </BrowserRouter>
    </>
  );
};

export default RouterPage;
